const AUTOPLAY_MS = 5000;
const DRAG_THRESHOLD = 50;
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

export const initAboutCarousel = (root = document) => {
  const carousels = root.querySelectorAll(".js-about-carousel");
  carousels.forEach((carousel) => {
    if (!(carousel instanceof HTMLElement)) return;
    if (carousel.dataset.initialized === "true") return;

    const track = carousel.querySelector(".c-about-carousel__track");
    const slides = Array.from(carousel.querySelectorAll(".c-about-carousel__slide"));
    const prevBtn = carousel.querySelector(".c-about-carousel__arrow--prev");
    const nextBtn = carousel.querySelector(".c-about-carousel__arrow--next");
    const dots = Array.from(carousel.querySelectorAll(".c-about-carousel__dot"));

    if (!(track instanceof HTMLElement) || slides.length < 2) return;

    carousel.dataset.initialized = "true";
    let index = Math.max(
      0,
      slides.findIndex((slide) => slide.classList.contains("is-active"))
    );
    let timer = null;
    let startX = 0;
    let currentX = 0;
    let dragging = false;
    const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);

    const render = (animate = true) => {
      track.style.transition = animate ? "transform 0.4s ease" : "none";
      track.style.transform = `translateX(${-index * 100}%)`;
      slides.forEach((slide, i) => {
        slide.classList.toggle("is-active", i === index);
      });
      dots.forEach((dot, i) => {
        dot.classList.toggle("is-active", i === index);
        dot.setAttribute("aria-current", i === index ? "true" : "false");
      });
    };

    const goTo = (nextIndex, animate = true) => {
      index = (nextIndex + slides.length) % slides.length;
      render(animate);
    };

    const syncIndexFromDom = () => {
      const activeIndex = slides.findIndex((slide) =>
        slide.classList.contains("is-active")
      );
      if (activeIndex >= 0) {
        index = activeIndex;
      }
    };

    const next = () => goTo(index + 1);
    const prev = () => goTo(index - 1);

    const stopAutoplay = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const startAutoplay = () => {
      if (reducedMotion.matches) return;
      stopAutoplay();
      timer = setInterval(() => {
        syncIndexFromDom();
        next();
      }, AUTOPLAY_MS);
    };

    const onPointerDown = (event) => {
      if (event.button !== 0) return;
      if (event.target instanceof Element && event.target.closest(".c-about-carousel__arrow")) {
        return;
      }
      dragging = true;
      startX = event.clientX;
      currentX = startX;
      stopAutoplay();
      track.style.transition = "none";
      carousel.setPointerCapture?.(event.pointerId);
    };

    const onPointerMove = (event) => {
      if (!dragging) return;
      currentX = event.clientX;
    };

    const onPointerUp = (event) => {
      if (!dragging) return;
      dragging = false;
      if (event && "pointerId" in event) {
        carousel.releasePointerCapture?.(event.pointerId);
      }
      const delta = currentX - startX;
      if (Math.abs(delta) > DRAG_THRESHOLD) {
        if (delta < 0) next();
        else prev();
      } else {
        render(true);
      }
      startAutoplay();
    };

    const onPointerLeave = () => {
      if (!dragging) return;
      onPointerUp();
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        stopAutoplay();
      } else {
        startAutoplay();
      }
    };

    prevBtn?.addEventListener("click", () => {
      syncIndexFromDom();
      prev();
      startAutoplay();
    });

    nextBtn?.addEventListener("click", () => {
      syncIndexFromDom();
      next();
      startAutoplay();
    });

    dots.forEach((dot) => {
      dot.addEventListener("click", () => {
        const targetIndex = Number(dot.getAttribute("data-slide"));
        if (Number.isNaN(targetIndex)) return;
        goTo(targetIndex);
        startAutoplay();
      });
    });

    carousel.addEventListener("pointerdown", onPointerDown);
    carousel.addEventListener("pointermove", onPointerMove);
    carousel.addEventListener("pointerup", onPointerUp);
    carousel.addEventListener("pointercancel", onPointerUp);
    carousel.addEventListener("pointerleave", onPointerLeave);
    carousel.addEventListener("mouseenter", stopAutoplay);
    carousel.addEventListener("mouseleave", startAutoplay);
    document.addEventListener("visibilitychange", onVisibilityChange);

    render(false);
    startAutoplay();
  });
};
